import {
  BatchUploadMetadata,
  FeatureFlags,
  FlowType,
  UploadDocumentJob,
  UploadSource,
} from '@domain/types';
import { v4 as uuid } from 'uuid';
import type { FileImportCompletedParams } from './tracking.upload';
import { toUploadErrorCause } from './uploadErrorCause';
import { segmentUploadTracking } from './uploadSegmentTracking';

export type { BatchUploadMetadata };

export type BatchEndMode = 'immediate' | 'awaitAnalysis';

export function getBatchEndMode(
  flowType: UploadDocumentJob['flowType'],
  featureFlags: FeatureFlags,
): BatchEndMode {
  return flowType === 'sending' && featureFlags['enable-new-import-analyze-flow']
    ? 'awaitAnalysis'
    : 'immediate';
}

/** Groups finished jobs by batchUploadId. Jobs without a batchUploadId are skipped. */
export function groupJobsByBatch(jobs: UploadDocumentJob[]): Map<string, UploadDocumentJob[]> {
  const groups = new Map<string, UploadDocumentJob[]>();
  for (const job of jobs) {
    if (!job.batchUploadId) continue;
    const group = groups.get(job.batchUploadId) ?? [];
    group.push(job);
    groups.set(job.batchUploadId, group);
  }
  return groups;
}

export interface UploadTrackingContext {
  uploadJobId: string;
  startDate: Date;
  uploadSource: UploadSource;
  flowType: UploadDocumentJob['flowType'];
  batchUploadId?: string;
}

interface CreateUploadTrackingContextParams {
  uploadSource: UploadSource;
  flowType: UploadDocumentJob['flowType'];
  batchUploadId?: string;
}

interface BatchUploadState extends BatchUploadMetadata {
  startDate: Date;
}

export function createBatchUploadState(fileCount: number): BatchUploadState {
  const isBatch = fileCount > 1;
  const startDate = new Date();

  return {
    batchUploadId: isBatch ? uuid() : undefined,
    batchStartDate: isBatch ? startDate : undefined,
    batchDocumentsCount: isBatch ? fileCount : undefined,
    startDate,
  };
}

export function trackBatchUploadStart(
  batch: BatchUploadState,
  uploadSource: UploadSource,
  documentsCount: number,
): void {
  if (!batch.batchUploadId) return;

  segmentUploadTracking().trackBatchFileUploadStart({
    documentsCount,
    uploadSource,
    batchUploadId: batch.batchUploadId,
  });
}

export function trackBatchUploadEnd(params: {
  batchUploadId: string;
  documentsCount: number;
  startDate: Date;
}): void {
  if (params.documentsCount <= 1) return;

  segmentUploadTracking().trackBatchFileUploadEnd({
    documentsCount: params.documentsCount,
    batchUploadId: params.batchUploadId,
    startDate: params.startDate,
  });
}

/** Starts batch tracking and returns metadata to attach to upload jobs. */
export function beginUploadBatch(
  fileCount: number,
  uploadSource: UploadSource,
): BatchUploadMetadata {
  const batch = createBatchUploadState(fileCount);
  trackBatchUploadStart(batch, uploadSource, fileCount);

  return {
    batchUploadId: batch.batchUploadId,
    batchStartDate: batch.batchStartDate,
    batchDocumentsCount: batch.batchDocumentsCount,
  };
}

export function getStartupBatchAnalyticsSource(flowType: FlowType): UploadSource {
  return flowType === 'sending' ? 'opening' : 'watcher';
}

export function batchStartDateFromJobs(jobs: UploadDocumentJob[]): Date {
  const batchStart = jobs.find((job) => job.batchStartDate)?.batchStartDate;
  if (batchStart) return batchStart;

  return jobs.reduce((min, job) => (job.startDate < min ? job.startDate : min), jobs[0].startDate);
}

export function createUploadTrackingContext(
  params: CreateUploadTrackingContextParams,
): UploadTrackingContext {
  return {
    uploadJobId: uuid(),
    startDate: new Date(),
    uploadSource: params.uploadSource,
    flowType: params.flowType,
    batchUploadId: params.batchUploadId,
  };
}

export function uploadTrackingContextFromJob(job: UploadDocumentJob): UploadTrackingContext {
  return {
    uploadJobId: job.id,
    startDate: job.startDate,
    uploadSource: job.uploadSource,
    flowType: job.flowType,
    batchUploadId: job.batchUploadId,
  };
}

export function trackUploadStarted(ctx: UploadTrackingContext): void {
  segmentUploadTracking().trackFileUploadStarted({
    uploadJobId: ctx.uploadJobId,
    uploadSource: ctx.uploadSource,
    flowType: ctx.flowType,
    batchUploadId: ctx.batchUploadId,
  });
}

export function trackUploadSucceeded(ctx: UploadTrackingContext, documentId: string): void {
  segmentUploadTracking().trackFileUploaded({
    documentId,
    uploadSource: ctx.uploadSource,
    uploadJobId: ctx.uploadJobId,
    startDate: ctx.startDate,
    flowType: ctx.flowType,
    batchUploadId: ctx.batchUploadId,
  });
}

export function trackUploadFailed(ctx: UploadTrackingContext, errorCause?: unknown): void {
  segmentUploadTracking().trackFileUploadFailed({
    errorCause: errorCause !== undefined ? toUploadErrorCause(errorCause) : undefined,
    uploadJobId: ctx.uploadJobId,
    startDate: ctx.startDate,
    flowType: ctx.flowType,
    batchUploadId: ctx.batchUploadId,
  });
}

export function trackFileImportCompleted(params: FileImportCompletedParams): void {
  segmentUploadTracking().trackFileImportCompleted(params);
}

export async function withUploadTracking<T>(
  params: CreateUploadTrackingContextParams,
  run: () => Promise<{ documentId: string; value: T }>,
): Promise<T> {
  const ctx = createUploadTrackingContext(params);

  trackUploadStarted(ctx);

  try {
    const { documentId, value } = await run();
    trackUploadSucceeded(ctx, documentId);
    return value;
  } catch (error) {
    trackUploadFailed(ctx, error);
    throw error;
  }
}

/**
 * Runs a multi-file upload outside Redux with a shared batch session (integration modal).
 */
export async function runBatchUpload<T>(
  fileCount: number,
  uploadSource: UploadSource,
  uploadFn: (batchUploadId: string | undefined) => Promise<T>,
): Promise<T> {
  const batch = createBatchUploadState(fileCount);
  trackBatchUploadStart(batch, uploadSource, fileCount);

  try {
    return await uploadFn(batch.batchUploadId);
  } finally {
    if (batch.batchUploadId) {
      trackBatchUploadEnd({
        batchUploadId: batch.batchUploadId,
        documentsCount: fileCount,
        startDate: batch.startDate,
      });
    }
  }
}
